import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brokerageAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import './BrokerageCreate.css';

const BrokerageView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [brokerage, setBrokerage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadBrokerage();
  }, [id]);

  const loadBrokerage = async () => {
    try {
      setLoading(true);
      const response = await brokerageAPI.getById(id);
      const data = response.data;
      const dateStr = new Date(data.date).toISOString().split('T')[0];
      setBrokerage({
        ...data,
        date: dateStr,
        commissionAmount: data.commissionAmount || '',
      });
    } catch (err) {
      setErrorMessage(t('brokerage.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!brokerage) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('brokerage.list.messages.noData')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <i className="fas fa-eye page-icon"></i>
          <h1>{t('brokerage.view.title')}</h1>
        </div>
        <div className="page-header-actions">
          <button className="btn-back" onClick={() => navigate('/brokerage')}>
            <i className="fas fa-arrow-left"></i> {t('brokerage.view.backToList')}
          </button>
          <button className="btn-edit" onClick={() => navigate(`/brokerage/edit/${id}`)}>
            <i className="fas fa-edit"></i> {t('brokerage.view.edit')}
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('brokerage.view.fields.date')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={brokerage.date}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-dollar-sign"></i> {t('brokerage.view.fields.commissionAmount')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-dollar-sign input-icon"></i>
              <input
                type="number"
                step="0.01"
                className="form-input"
                value={brokerage.commissionAmount}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-map-marker-alt"></i> {t('brokerage.view.fields.unitLocation')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-map-marker-alt input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={brokerage.unitLocation || ''}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user"></i> {t('brokerage.view.fields.ownerName')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={brokerage.ownerName || ''}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-handshake"></i> {t('brokerage.view.fields.brokerName')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-handshake input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={brokerage.brokerName || ''}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group form-group-full">
            <label className="form-label">
              <i className="fas fa-align-left"></i> {t('brokerage.view.fields.notes')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-align-left input-icon"></i>
              <textarea
                className="form-input"
                value={brokerage.notes || ''}
                readOnly
                disabled
                rows="3"
              />
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default BrokerageView;

